import { useState } from 'react';
import { FieldPath, FieldValues } from 'react-hook-form';
import * as Combobox from '@givebutter/primitives/combobox';
import { Loader } from '@givebutter/primitives/loader';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Form from 'components/form';
import { UseFormComboboxProps } from 'components/form/UseFormCombobox';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import useDebounce from 'customHooks/useDebounce';
import { useInfiniteAppeals } from 'customHooks/useInfiniteAppeals';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { twMerge } from 'tailwind-merge';
import { Appeal } from 'types/domain';

const NewAppealId = '-1';

interface SelectAppealProps<T extends FieldValues, K extends FieldPath<T>>
    extends UseFormComboboxProps<T, K> {}

const SelectAppeal = <T extends FieldValues, K extends FieldPath<T>>({
    children,
    control,
    name,
    ...props
}: SelectAppealProps<T, K>) => {
    const [query, setQuery] = useState('');
    const debouncedSearchAppeals = useDebounce(query);
    const { accountId } = useCurrentAccount();

    const {
        data: appealsData,
        ref: infiniteScrollRef,
        isFetchingNextPage,
        isFetching,
    } = useInfiniteAppeals({ query: debouncedSearchAppeals });

    const { watch } = Form.useFormContext();
    const defaultAppealValue = watch(name);
    const defaultAppealId = defaultAppealValue.length > 0 ? parseInt(defaultAppealValue[0]) : -1;

    const defaultAppeal = queryClient.getQueryData<Appeal | null>(
        accountQueryKeys.appealById(accountId, defaultAppealId),
    );

    const appeals = appealsData?.pages ?? (defaultAppeal ? [defaultAppeal] : []);

    return (
        <Form.Combobox control={control} name={name} allowMultiple={false} {...props}>
            <Combobox.Trigger aria-label="Select appeal code">
                <Combobox.Select placeholder="Select appeal code" />
            </Combobox.Trigger>
            <Combobox.Popover>
                <Combobox.Search value={query} onChangeValue={setQuery} isLoading={isFetching} />
                <Combobox.Items>
                    {children}
                    {appeals.map(appeal => (
                        <Combobox.Item key={appeal.id} value={`${appeal.id}`}>
                            <Combobox.ItemText>{appeal.name}</Combobox.ItemText>
                            <Combobox.CheckmarkIndicator />
                        </Combobox.Item>
                    ))}
                    {appeals.length === 0 && <Combobox.NoneFoundText />}
                    <div ref={infiniteScrollRef} />
                    {isFetchingNextPage && (
                        <div className="flex items-center justify-center p-4">
                            <Loader variant={'static'} />
                        </div>
                    )}
                </Combobox.Items>
            </Combobox.Popover>
        </Form.Combobox>
    );
};

const NewAppealItem = ({ className, ...props }: Omit<Combobox.ComboboxItemProps, 'value'>) => {
    return (
        <Combobox.Item
            className={twMerge('flex items-center gap-2', className)}
            value={NewAppealId}
            {...props}
        >
            <FontAwesomeIcon icon={faPlus} />
            <span>New appeal code</span>
        </Combobox.Item>
    );
};

export { SelectAppeal, NewAppealItem, NewAppealId };
